/**
 * View User Page
 *
 * Purpose: Display user details in a read-only view
 */

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  User,
  FileText,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { LoadingSpinner } from '@/features/common/components/LoadingSpinner'
import { useUserDetailViewModel } from '../viewmodels/use_user_detail_viewmodel'

export function ViewUserPageView() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { state, isLoading, error } = useUserDetailViewModel(id)
  const userData = state.user
  const [imageError, setImageError] = useState(false)

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  // Format date of birth
  const formatDateOfBirth = (dateString?: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  // Format phone number
  const formatPhoneNumber = (countryCode?: string, phoneNo?: string) => {
    if (!phoneNo) return 'N/A'
    if (countryCode) {
      return `${countryCode} ${phoneNo}`
    }
    return phoneNo
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error && !userData) {
    return (
      <div className="text-center p-4 text-red-600">
        {error}
        <p className="text-sm text-gray-500 mt-2">
          Please ensure your JSON server is running on port 3001.
        </p>
      </div>
    )
  }

  if (!userData) {
    return <div className="text-center p-4 text-gray-600">User not found</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/users')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">User Details</h1>
          <p className="text-sm text-gray-500 mt-1">View user information</p>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-teal-50 to-teal-100 px-6 py-8 border-b border-gray-200">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="relative">
              {userData.imageUrl && !imageError ? (
                <img
                  src={userData.imageUrl}
                  alt={userData.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-teal-500 border-4 border-white shadow-lg flex items-center justify-center text-white text-3xl font-semibold">
                  {userData.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
              {/* Status Badge */}
              <div className="absolute bottom-0 right-0">
                {userData.status === 'active' ? (
                  <div className="w-6 h-6 bg-green-500 rounded-full border-4 border-white flex items-center justify-center">
                    <CheckCircle className="w-3 h-3 text-white" />
                  </div>
                ) : (
                  <div className="w-6 h-6 bg-gray-400 rounded-full border-4 border-white flex items-center justify-center">
                    <XCircle className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            </div>

            {/* User Info */}
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{userData.name}</h2>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">{userData.email}</span>
                </div>
                {userData.phoneNo && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span className="text-sm">
                      {formatPhoneNumber(userData.countryCode, userData.phoneNo)}
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-3">
                <span
                  className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    userData.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                  style={
                    userData.status === 'active'
                      ? { backgroundColor: '#019b9a', color: '#ffffff' }
                      : undefined
                  }
                >
                  {userData.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Details Section */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Personal Information
              </h3>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Full Name
                  </label>
                  <p className="mt-1 text-sm text-gray-900">{userData.name}</p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Email Address
                  </label>
                  <p className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {userData.email}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Phone Number
                  </label>
                  <p className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    {formatPhoneNumber(userData.countryCode, userData.phoneNo)}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Gender
                  </label>
                  <p className="mt-1 text-sm text-gray-900 capitalize">
                    {userData.gender || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Additional Information
              </h3>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Date of Birth
                  </label>
                  <p className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {formatDateOfBirth(userData.dateOfBirth)}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Account Created
                  </label>
                  <p className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {formatDate(userData.createdAt)}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    User ID
                  </label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{userData.id}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bio Section */}
          {userData.bio && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Biography
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg">
                {userData.bio}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-end gap-4">
            <button
              onClick={() => navigate('/admin/users')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Back to Users
            </button>
            <button
              onClick={() => navigate(`/admin/users/${userData.id}/edit`)}
              className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#019b9a' }}
            >
              Edit User
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
